import { forgetSessionHint } from "./sessionHint.ts";
import { webStorage } from "./webStorage.ts";

/**
 * Bearer-token session, the same auth model as iOS/Android: JWT in
 * localStorage, sent as Authorization: Bearer. No cookies — the SSR server
 * never sees the token and stays stateless. The p3 auth store writes this
 * key; this module is the read side shared with route guards.
 */
export const TOKEN_KEY = "selfeed.web.token";

type TokenStorage = Pick<Storage, "getItem">;

// Through `webStorage`, not a `typeof` guard: a browser that blocks site
// data throws from the getter, and this module is read while the auth
// store is evaluated — the throw took the client bundle down with it.
function defaultStorage(): TokenStorage | null {
  return webStorage("local");
}

export function readToken(storage: TokenStorage | null = defaultStorage()): string | null {
  const value = storage?.getItem(TOKEN_KEY)?.trim();
  return value ? value : null;
}

export function hasSession(storage: TokenStorage | null = defaultStorage()): boolean {
  return readToken(storage) !== null;
}

type WritableTokenStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultWritableStorage(): WritableTokenStorage | null {
  return webStorage("local");
}

export function writeToken(
  token: string,
  storage: WritableTokenStorage | null = defaultWritableStorage(),
): void {
  storage?.setItem(TOKEN_KEY, token);
}

export function clearToken(storage: WritableTokenStorage | null = defaultWritableStorage()): void {
  storage?.removeItem(TOKEN_KEY);
}

export const REFRESH_KEY = "selfeed.web.refresh";

export function readRefreshToken(storage: TokenStorage | null = defaultStorage()): string | null {
  const value = storage?.getItem(REFRESH_KEY)?.trim();
  return value ? value : null;
}

export function writeRefreshToken(
  token: string,
  storage: WritableTokenStorage | null = defaultWritableStorage(),
): void {
  storage?.setItem(REFRESH_KEY, token);
}

export function clearRefreshToken(
  storage: WritableTokenStorage | null = defaultWritableStorage(),
): void {
  storage?.removeItem(REFRESH_KEY);
}

/**
 * What the page hears when the server has said this session is over.
 *
 * The verdict arrives inside a request — the api middleware's refresh, an
 * upload hook's — far from anything that can navigate. The listener is
 * `useSignOutWhenSessionEnds`, on the root: it empties the store and takes
 * the tab to the door, the way the phone signs out the moment its refresh
 * says the pair is dead.
 */
export const SESSION_ENDED_EVENT = "selfeed:session-ended";

/**
 * The tombstone, and the word that it was laid. Both halves go — the route
 * guards read their absence on the next navigation — and the page is told,
 * so the tab does not have to wait for one. Not a sign-out somebody chose:
 * that is the auth store's `signOut`, which is quiet, because the person
 * pressing it is the one who would be told.
 */
export function endSession(storage: WritableTokenStorage | null = defaultWritableStorage()): void {
  clearToken(storage);
  clearRefreshToken(storage);
  forgetSessionHint();
  if (typeof window !== "undefined") window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
}
